"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { createContext, PropsWithChildren, useContext, useEffect, useRef, useState } from "react";

import { env, isProduction } from "@/env";
import { useAuthState } from "@/hooks/use-auth-state";
import { useDecibelWallet } from "@/hooks/use-decibel-wallet";
import { Analytics } from "@/lib/analytics";
import { attributionTraits, readAttribution } from "@/lib/analytics/attribution";
import { MARKETING_PARAMS, sanitizeParams } from "@/lib/analytics/url-allowlist";
import { addressesEqual } from "@/utils/address";
import { Logger } from "@/utils/logger";

import { useCountry } from "./trading-allowed";

const AnalyticsContext = createContext<Analytics | null>(null);

export const HandleWalletAnalytics = () => {
  const {
    chainName,
    ownerWalletAddress,
    accountAddress: _accountAddress,
    wallet,
    keylessInfo,
    isEvmChainLoading,
  } = useDecibelWallet();
  const { subaccountAddress: _subaccountAddress } = useAuthState();
  const { country } = useCountry();
  const Analytics = useAnalytics();
  const subaccountAddress = _subaccountAddress?.toLowerCase();
  const accountAddress = _accountAddress?.toLowerCase();
  // Keyless (social login) sessions have no injected `wallet`, so fall back to a
  // `keyless-<provider>` name — otherwise these connects report walletName:
  // undefined and are indistinguishable from missing data in the funnel.
  const walletName =
    wallet?.name.toLowerCase() ?? (keylessInfo ? `keyless-${keylessInfo.provider}` : undefined);

  useEffect(() => {
    if (!Analytics.rudder) return;

    const user = {
      account_address: accountAddress,
      chain: chainName.toLowerCase(),
      owner_address: ownerWalletAddress,
      wallet_name: walletName,
    };

    if (user.account_address) {
      Analytics.rudder.identify(user.account_address, {
        ...user,
        ...attributionTraits(readAttribution()),
      });
    }
    if (walletName) Analytics.rudder.group(walletName);
    if (subaccountAddress) Analytics.rudder.group(subaccountAddress);

    // pwa_mode and platform are auto-attached per-event inside trackEvent so
    // they stay fresh across install transitions — don't snapshot them here.
    Analytics.setAdditionalEventProps({
      ...user,
      subaccount_address: subaccountAddress,
      wallet_name: walletName,
      country,
    });

    return Analytics.resetAdditionalEventProps;
  }, [
    walletName,
    subaccountAddress,
    accountAddress,
    chainName,
    ownerWalletAddress,
    Analytics,
    country,
  ]);

  // Fire a `wallet_connected` event once per newly-connected account. Tracked
  // separately from identify/group above so it emits exactly on connect (and on
  // account switch), not on every super-property change.
  //
  // New-vs-returning is left to the analytics tool (Mixpanel computes it from
  // identity), so we don't derive a first-connect flag here.
  const lastConnectedAccountRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!Analytics.rudder) return;
    if (!accountAddress) {
      lastConnectedAccountRef.current = undefined;
      return;
    }
    // For EVM wallets the derived account address is available before the chain
    // id resolves, during which `chainName` defaults to "Aptos". Wait for the
    // chain to settle before recording the account, otherwise the dedupe ref
    // locks in the event with the wrong `chain` and never re-fires to correct it.
    if (isEvmChainLoading) return;
    if (addressesEqual(lastConnectedAccountRef.current, accountAddress)) return;
    lastConnectedAccountRef.current = accountAddress;

    Analytics.wallet.connected({
      walletName,
      accountAddress,
      chain: chainName.toLowerCase(),
    });
  }, [accountAddress, walletName, chainName, isEvmChainLoading, Analytics]);

  return null;
};

export const HandlePageViewAnalytics = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const Analytics = useAnalytics();

  const stringifiedSearchParams = searchParams.toString();

  useEffect(() => {
    if (!Analytics.rudder) return;

    Analytics.ui.pageViewed({
      pageName: pathname.split("/").filter(Boolean).join("/") || "home",
      path: pathname,
      queryParams: sanitizeParams(searchParams, { isProduction }),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, Analytics, stringifiedSearchParams]);

  return null;
};

const GTAG_POLL_MS = 100;
const GTAG_FALLBACK_MS = 5000;

function stripMarketingParams() {
  const params = new URLSearchParams(window.location.search);
  if (!MARKETING_PARAMS.some((key) => params.has(key))) return;
  for (const key of MARKETING_PARAMS) params.delete(key);
  const qs = params.toString();
  window.history.replaceState(
    window.history.state,
    "",
    `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`,
  );
}

function hasGtagConfig(dataLayer: unknown[]) {
  return dataLayer.some(
    (entry) =>
      typeof entry === "object" && entry !== null && (entry as ArrayLike<unknown>)[0] === "config",
  );
}

// gtag reads marketing params from the URL on `config`; a function pushed after that entry runs
// once it is processed. The config script can land after this effect, so poll, with a fallback.
// The fallback stays armed even once queued: gtag.js itself (not just its config push) can be
// blocked by an ad blocker, in which case the queued callback is never invoked.
function stripMarketingParamsAfterGtag(): () => void {
  const w = window as unknown as { dataLayer?: unknown[] };
  let poll: number | undefined;
  let stripped = false;

  function stopAll() {
    if (poll !== undefined) window.clearInterval(poll);
    window.clearTimeout(fallback);
  }

  function stripOnce() {
    if (stripped) return;
    stripped = true;
    stopAll();
    stripMarketingParams();
  }

  function queueStrip() {
    if (!w.dataLayer || !hasGtagConfig(w.dataLayer)) return false;
    w.dataLayer.push(stripOnce);
    return true;
  }

  const fallback = window.setTimeout(stripOnce, GTAG_FALLBACK_MS);

  if (!queueStrip()) {
    poll = window.setInterval(() => {
      if (queueStrip() && poll !== undefined) {
        window.clearInterval(poll);
        poll = undefined;
      }
    }, GTAG_POLL_MS);
  }

  return stopAll;
}

export function AnalyticsProvider({
  children,
  gaEnabled = false,
}: PropsWithChildren<{ gaEnabled?: boolean }>) {
  const [analytics, setAnalytics] = useState<Analytics>(new Analytics());

  useEffect(() => {
    async function loadAnalytics() {
      const { RudderAnalytics } = await import("@rudderstack/analytics-js");

      const rudderAnalyticsInstance = new RudderAnalytics();

      if (env.NEXT_PUBLIC_RUDDERSTACK_WRITE_KEY && env.NEXT_PUBLIC_RUDDERSTACK_DATA_PLANE_URL) {
        rudderAnalyticsInstance.load(
          env.NEXT_PUBLIC_RUDDERSTACK_WRITE_KEY,
          env.NEXT_PUBLIC_RUDDERSTACK_DATA_PLANE_URL,
          { logLevel: isProduction ? "NONE" : "DEBUG" },
        );
      }

      const analytics = new Analytics(rudderAnalyticsInstance);

      setAnalytics(analytics);
    }

    void loadAnalytics();
  }, []);

  useEffect(() => {
    if (!analytics.rudder) return;

    try {
      // Recorded before hydration; send once rudder is up.
      const attribution = readAttribution();
      if (attribution) analytics.rudder.identify(attributionTraits(attribution));

      // RudderStack's first page() has read the marketing params; gtag may still need them.
      const hasMarketingParams = MARKETING_PARAMS.some((key) =>
        new URLSearchParams(window.location.search).has(key),
      );

      // Register initial referrer once (if not already registered)
      const initialReferrer = document.referrer;
      const savedReferrer = analytics.rudder.getUserTraits()?.referrer;
      if (!savedReferrer && initialReferrer) {
        analytics.rudder.identify({ referrer: initialReferrer });
      }

      if (!hasMarketingParams) return;
      if (!gaEnabled) {
        stripMarketingParams();
        return;
      }
      return stripMarketingParamsAfterGtag();
    } catch (error) {
      Logger.error(error, { message: "Failed to register attribution" });
    }
  }, [analytics, gaEnabled]);

  return <AnalyticsContext.Provider value={analytics}>{children}</AnalyticsContext.Provider>;
}

export function useAnalytics() {
  const context = useContext(AnalyticsContext);
  if (!context) throw new Error("useAnalytics must be used within an AnalyticsProvider");
  return context;
}

// Stable ref to the latest Analytics instance. AnalyticsProvider swaps in a
// loaded RudderAnalytics instance asynchronously, so once-per-mount effects
// or stable callbacks that close over `analytics` would otherwise re-fire or
// double-track events. Read `ref.current` inside handlers/effects.
export function useAnalyticsRef() {
  const analytics = useAnalytics();
  const ref = useRef<Analytics>(analytics);
  useEffect(() => {
    ref.current = analytics;
  }, [analytics]);
  return ref;
}
